/**
 * composerMentionPills — a mention in the DRAFT BOX looks like a mention
 * (Miles, 2026-09-22).
 *
 * His ask, verbatim: "when a mention happens in the draft box the same thing
 * must happen. this helps the user know this is a confirmed user handle as
 * well before sending it."
 *
 * WHAT THIS IS NOW, AND WHAT IT IS NOT (revised 2026-09-22). This module
 * paints a pill behind the handle STILL BEING TYPED. The handle the caret has
 * left becomes a real chip — the person's glyph and full name — and that is
 * composerChipSync.ts's job.
 *
 * The revision is recorded rather than quietly rewritten, because the header
 * that stood here said the composer could not hold a chip AT ALL, for two
 * reasons that were both true when it was written:
 *
 *   · `PromptInput`'s hard containment wall for pasted markup —
 *     `& * { background: transparent !important; border-radius: 0 !important;
 *     position: static !important }` (owner report 2026-08-26, a pasted
 *     message that rendered as a bubble hanging off the right edge) — which a
 *     chip would be a descendant of;
 *   · the send path, the sanitizer, the caret restore and the draft
 *     persistence all reading that element's own TEXT, which a chip showing a
 *     NAME would silently change.
 *
 * Miles asked for the element three times, so both were re-answered instead
 * of restated. The wall is out-specified rather than weakened (see
 * `mentionChipStyles`' `important` flag), and `htmlToPlainText` — the ONE
 * door every reader of the draft passes through — now emits `@handle` for a
 * chip, with the handle carried in the chip's own text as a second belt. What
 * is SENT is unchanged to the byte, which is the property the whole design
 * hangs on and the one its tests spend most of their rows on.
 *
 * This module is still measurement-only and still touches nothing.
 *
 * WHY MEASURED, NOT MIRRORED. The obvious alternative — a second element
 * rendering the same string under the same font, the `::after` auto-grow
 * replica's trick — has to reproduce the browser's line breaking exactly, and
 * that replica's own history is the reason not to trust it for per-character
 * position: it overshot badly after rich pastes until it was fed plain text
 * (owner report 2026-07-14), because the composer holds markup and the mirror
 * holds a string. A pill is only right if it is where the glyphs ACTUALLY are.
 * So the geometry comes from `Range.getClientRects()` over the live text —
 * whatever structure a paste left around it, whatever the wrapping, whatever
 * the scroll position, the pill lands on the glyphs. He had just reported
 * "slight oddities in the text lining up"; a mirror is how you get more of
 * those.
 *
 * Pure. No React, no DOM construction — the caller does the walking and hands
 * the measurements in. Rect maths and offsets are the half that can be tested
 * without a browser, so they live here.
 */
import { splitMentionParts } from './mentionChip';
import { isComposerChip, CHIP_PLACEHOLDER } from './composerChip';
import type { MentionChipPerson } from './mentionChip';

/** Where one resolved mention sits in a string, and who it resolved to. */
export interface MentionSpanOffsets {
  /** Index of the `@`. */
  start: number;
  /** Exclusive end — the last character of the handle, plus one. */
  end: number;
  /** The handle without `@`, trailing punctuation already stripped. */
  handle: string;
  /** The person it confirms, for the pill's accessible name. */
  name: string;
}

/**
 * Character offsets of every RESOLVED mention in `text`.
 *
 * Read off `splitMentionParts`, never a second regex: the draft box and the
 * sent message must agree about what a mention is, or the confirmation the
 * pill gives is a confirmation of something else. The parts' concatenation
 * invariant is what makes the offsets exact.
 */
export function mentionOffsetsIn(
  text: string,
  resolve: (handle: string) => MentionChipPerson | null,
): MentionSpanOffsets[] {
  const out: MentionSpanOffsets[] = [];
  let at = 0;
  for (const part of splitMentionParts(text, resolve)) {
    if (part.kind === 'text') {
      at += part.text.length;
      continue;
    }
    out.push({ start: at, end: at + part.raw.length, handle: part.handle, name: part.person.name });
    at += part.raw.length;
  }
  return out;
}

/** The subset of DOMRect this module reads — so it is testable with plain objects. */
export interface RectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** One pill to paint, in coordinates relative to the overlay's own box. */
export interface MentionPillBox {
  left: number;
  top: number;
  width: number;
  height: number;
  handle: string;
  name: string;
  /**
   * The glyphs' own edges, before the pad — the floor `separatePills` may
   * never shrink a pill past. A pill narrower than its text would clip the
   * characters it is meant to be confirming.
   */
  textLeft: number;
  textRight: number;
}

/**
 * How far the pill reaches past the glyphs it sits behind, in CSS pixels.
 *
 * Small on purpose, and the reason is arithmetic rather than taste: a space at
 * the composer's size is about 4px, so that is ALL the room there is between a
 * handle and the next word. Anything much larger and the pill closes the gap
 * to "check" in "@malaika check" and reads as though it had swallowed it —
 * which is also why the pill is a rounded rectangle rather than the capsule
 * the transcript's chip is (see the Pill's own note). Vertically, the
 * composer's line-height is the whole budget.
 */
export const PILL_PAD_X = 2.5;
export const PILL_PAD_Y = 1.5;

/**
 * Convert measured client rects into overlay-relative pill boxes.
 *
 * `base` is the overlay's own client rect. One mention can measure as SEVERAL
 * rects when it wraps across a line; each becomes its own pill, which is why
 * the pill's CSS clones its decoration rather than rounding only the ends.
 *
 * A zero-area rect is dropped — `getClientRects()` emits them for collapsed
 * ranges and they would paint as a hairline.
 */
export function pillBoxesFrom(
  rects: readonly RectLike[],
  base: RectLike,
  span: { handle: string; name: string },
): MentionPillBox[] {
  const out: MentionPillBox[] = [];
  for (const r of rects) {
    if (!(r.width > 0) || !(r.height > 0)) continue;
    const textLeft = r.left - base.left;
    out.push({
      left: textLeft - PILL_PAD_X,
      top: r.top - base.top - PILL_PAD_Y,
      width: r.width + PILL_PAD_X * 2,
      height: r.height + PILL_PAD_Y * 2,
      handle: span.handle,
      name: span.name,
      textLeft,
      textRight: textLeft + r.width,
    });
  }
  return out;
}

/**
 * The clear space kept between two pills on one line, in CSS pixels.
 *
 * Small, because the space it has to fit inside is small.
 */
export const PILL_MIN_GAP = 2;

/**
 * Keep adjacent pills from merging into one bar.
 *
 * WHY THIS EXISTS. "@Prest0n @malaika" — which is exactly what the draft in
 * Miles's screenshot said — separates two handles by ONE SPACE, about 4px at
 * the composer's size. With `PILL_PAD_X` on each side the two boxes overlap by
 * ~3px and paint as a single continuous blob, so two confirmed people read as
 * one. Shrinking the pad until they clear would take it to ~0 and make every
 * pill hug its text, which is what the pad is there to avoid.
 *
 * So the pad is generous by default and YIELDS where it has to: two pills on
 * one line give up their facing pads, evenly, down to the glyphs' own edges
 * and no further. Where there is room — the ordinary case — nothing moves.
 *
 * Same-line is decided by the tops matching (a wrapped line is a different
 * row), and the input is in paint order, which for a left-to-right line is
 * also left-to-right.
 */
export function separatePills(pills: readonly MentionPillBox[]): MentionPillBox[] {
  const out = pills.map((p) => ({ ...p }));
  for (let i = 1; i < out.length; i += 1) {
    const left = out[i - 1];
    const right = out[i];
    if (Math.abs(left.top - right.top) > 1) continue; // different lines
    const leftEdge = left.left + left.width;
    const overlap = leftEdge + PILL_MIN_GAP - right.left;
    if (overlap <= 0) continue;

    // Each side gives up half, but neither may cross its own glyphs.
    const leftRoom = Math.max(0, leftEdge - left.textRight);
    const rightRoom = Math.max(0, right.textLeft - right.left);
    const giveLeft = Math.min(leftRoom, overlap / 2);
    const giveRight = Math.min(rightRoom, overlap - giveLeft);
    // If one side had no room, the other gives what it can.
    const extraLeft = Math.min(Math.max(0, leftRoom - giveLeft), overlap - giveLeft - giveRight);

    left.width -= giveLeft + extraLeft;
    right.left += giveRight;
    right.width -= giveRight;
  }
  return out;
}

/**
 * A run of text inside the composer, and where it starts in the flattened
 * draft — or a CHIP, which stands for exactly one character.
 *
 * ONE COORDINATE SYSTEM, and that is the point (2026-09-22). A chip is an
 * element with hidden text inside it, so a flatten that walked into it would
 * read the handle twice (once zero-sized, once as the name) and a flatten
 * that skipped it would leave two offsets meaning the same position — "before
 * the chip" and "after the chip" — which is precisely the ambiguity a caret
 * cannot survive. `\uFFFC` (OBJECT REPLACEMENT CHARACTER, what Unicode
 * provides for an embedded object in text) gives the chip one character of
 * its own, so the candidate scan, the pill geometry and the caret all measure
 * the same string.
 *
 * It also falls out that the scan can never re-chip a chip: the placeholder
 * is neither whitespace nor a handle character, so no mention opens on it.
 */
export interface FlatTextSegment {
  /** The text node this run came from, or the chip element it stands for. */
  node: Text | Element;
  /** Offset of this node's first character within the flattened text. */
  start: number;
  /** Present, and always 1, when this segment IS a chip. */
  chipLength?: 1;
}

/** The flattened draft plus the map back to the nodes it came from. */
export interface FlatComposerText {
  text: string;
  segments: FlatTextSegment[];
}

/**
 * Element names that put a LINE BREAK in the draft's plain text.
 *
 * It matters for the grammar and not for looks: a mention opens at the start
 * of the text or after whitespace, so "foo<br>@bar" has to flatten with the
 * newline in it or `@bar` reads as preceded by `o` and is never confirmed —
 * while the send path, which reads `htmlToPlainText`, resolves it and
 * notifies the person. The two must agree.
 */
const BLOCKS: ReadonlySet<string> = new Set([
  'DIV', 'P', 'LI', 'UL', 'OL', 'BLOCKQUOTE', 'PRE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TABLE', 'TR', 'SECTION', 'ARTICLE',
]);

/**
 * Flatten a contentEditable into the text a mention is read from, keeping the
 * map back to each text node.
 *
 * Offsets are GLOBAL rather than per-node deliberately: a handle the browser
 * has split across two text nodes (an edit in the middle of a word, a paste
 * seam) resolves correctly here, where reading each node alone would see
 * `@mala` + `ika` and could confirm a prefix that is not what will be sent.
 */
export function flattenComposerText(root: Node | null | undefined): FlatComposerText {
  const segments: FlatTextSegment[] = [];
  let text = '';

  const breakHere = (): void => {
    if (text.length > 0 && !text.endsWith('\n')) text += '\n';
  };

  const walk = (node: Node): void => {
    for (let child = node.firstChild; child; child = child.nextSibling) {
      if (child.nodeType === 3 /* TEXT_NODE */) {
        const value = child.textContent ?? '';
        if (!value) continue;
        segments.push({ node: child as Text, start: text.length });
        text += value;
        continue;
      }
      if (child.nodeType !== 1 /* ELEMENT_NODE */) continue;
      if (isComposerChip(child as Element)) {
        segments.push({ node: child as Element, start: text.length, chipLength: 1 });
        text += CHIP_PLACEHOLDER;
        continue;
      }
      const name = (child as Element).tagName;
      if (name === 'BR') {
        text += '\n';
        continue;
      }
      if (BLOCKS.has(name)) {
        breakHere();
        walk(child);
        breakHere();
        continue;
      }
      walk(child);
    }
  };

  if (root) walk(root);
  // A block's closing break is needed when inline content follows it
  // ("<div>a</div>b" is two lines) but is an artifact of this walk at the very
  // end of the draft. Dropping it costs nothing — a mention never opens after
  // the last character — and keeps the flattened text the text a reader sees.
  if (text.endsWith('\n')) text = text.slice(0, -1);
  return { text, segments };
}

/** A node + offset pair, as a Range boundary. */
export interface FlatPoint {
  /** A text node, or a chip's PARENT when the index lands beside a chip. */
  node: Text | Node;
  offset: number;
}

/**
 * The node and offset a global character index falls on.
 *
 * Null when the index lands on a synthesized newline rather than on real text
 * (a `<br>`, a block seam) — a mention never starts there, so the caller
 * simply skips it instead of guessing a boundary.
 */
export function pointAt(segments: readonly FlatTextSegment[], index: number): FlatPoint | null {
  for (const seg of segments) {
    if (seg.chipLength) {
      // A chip is one character with no inside. The boundary is in its
      // PARENT, before it or after it — which is the only pair of positions a
      // caret or a Range can legally take around a contenteditable=false
      // element anyway.
      const parent = seg.node.parentNode;
      if (!parent) continue;
      if (index === seg.start || index === seg.start + 1) {
        const siblings = Array.prototype.indexOf.call(parent.childNodes, seg.node);
        if (siblings < 0) continue;
        return { node: parent, offset: siblings + (index === seg.start ? 0 : 1) };
      }
      continue;
    }
    const len = (seg.node.textContent ?? '').length;
    // `<=` on the end so an exclusive end offset lands on the last node.
    if (index >= seg.start && index <= seg.start + len) {
      return { node: seg.node, offset: index - seg.start };
    }
  }
  return null;
}
